import { api } from "@/redux/api";

import {
	CreateSalesforceDealRequest,
	CreateSalesforceDealResponse,
	CreateSalesforceDealResponseSchema,
	SalesforceConversationSyncResponse,
	SalesforceConversationSyncResponseSchema,
	SalesforceDealConfigResponse,
	SalesforceDealConfigResponseSchema,
	SalesforceDealCreationTriggerResponse,
	SalesforceDealCreationTriggerResponseSchema,
	SalesforceLeadConfigResponse,
	SalesforceLeadConfigResponseSchema,
	SalesforcePipelinesResponse,
	SalesforcePipelinesResponseSchema,
	UpdateSalesforceConversationSync,
	UpdateSalesforceDealConfig,
	UpdateSalesforceDealCreationTrigger,
	UpdateSalesforceLeadConfig,
} from "../../pages/Company/schema/salesforceDeal";

/**
 * The opportunity/lead half of the Salesforce API surface: pipelines, both configuration
 * endpoints, the creation trigger, conversation sync and manual deal creation.
 *
 * Injected into the same slice as `salesforce.api.ts` and sharing its `"Salesforce"` tag —
 * split only so neither file approaches the 500-line cap the way `dynamics.api.ts` did.
 */
export const salesforceDealApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// ── Pipelines (= Opportunity record types) ───────────────────
		getSalesforcePipelines: builder.query<SalesforcePipelinesResponse, void>({
			query: () => ({
				url: "salesforce/pipelines",
				method: "GET",
				responseSchema: SalesforcePipelinesResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),

		// ── Opportunity-creation trigger (company-scoped only) ───────
		getSalesforceDealCreationTrigger: builder.query<SalesforceDealCreationTriggerResponse, void>({
			query: () => ({
				url: "salesforce/deal-creation-trigger",
				method: "GET",
				responseSchema: SalesforceDealCreationTriggerResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		updateSalesforceDealCreationTrigger: builder.mutation<
			SalesforceDealCreationTriggerResponse,
			UpdateSalesforceDealCreationTrigger
		>({
			query: (body) => ({ url: "salesforce/deal-creation-trigger", method: "PUT", body }),
			invalidatesTags: ["Salesforce", "Me", "Company"],
		}),

		// ── Opportunity / lead configuration ─────────────────────────
		getSalesforceDealConfig: builder.query<SalesforceDealConfigResponse, void>({
			query: () => ({
				url: "salesforce/deal-config",
				method: "GET",
				responseSchema: SalesforceDealConfigResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		saveSalesforceDealConfig: builder.mutation<
			SalesforceDealConfigResponse,
			UpdateSalesforceDealConfig
		>({
			query: (body) => ({ url: "salesforce/deal-config", method: "PUT", body }),
			invalidatesTags: ["Salesforce"],
		}),
		getSalesforceLeadConfig: builder.query<SalesforceLeadConfigResponse, void>({
			query: () => ({
				url: "salesforce/lead-config",
				method: "GET",
				responseSchema: SalesforceLeadConfigResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		saveSalesforceLeadConfig: builder.mutation<
			SalesforceLeadConfigResponse,
			UpdateSalesforceLeadConfig
		>({
			query: (body) => ({ url: "salesforce/lead-config", method: "PUT", body }),
			invalidatesTags: ["Salesforce"],
		}),

		// ── Conversation sync ────────────────────────────────────────
		getSalesforceConversationSync: builder.query<SalesforceConversationSyncResponse, void>({
			query: () => ({
				url: "salesforce/conversation-sync",
				method: "GET",
				responseSchema: SalesforceConversationSyncResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		updateSalesforceConversationSync: builder.mutation<
			SalesforceConversationSyncResponse,
			UpdateSalesforceConversationSync
		>({
			query: (body) => ({ url: "salesforce/conversation-sync", method: "PUT", body }),
			invalidatesTags: ["Salesforce"],
		}),

		// ── Manual opportunity / lead creation ───────────────────────
		createSalesforceDeal: builder.mutation<
			CreateSalesforceDealResponse,
			CreateSalesforceDealRequest
		>({
			query: (body) => ({
				url: "salesforce/deals/create",
				method: "POST",
				body,
				responseSchema: CreateSalesforceDealResponseSchema,
			}),
			invalidatesTags: ["Salesforce"],
		}),
	}),
});

export const {
	useGetSalesforcePipelinesQuery,
	useGetSalesforceDealCreationTriggerQuery,
	useUpdateSalesforceDealCreationTriggerMutation,
	useGetSalesforceDealConfigQuery,
	useSaveSalesforceDealConfigMutation,
	useGetSalesforceLeadConfigQuery,
	useSaveSalesforceLeadConfigMutation,
	useGetSalesforceConversationSyncQuery,
	useUpdateSalesforceConversationSyncMutation,
	useCreateSalesforceDealMutation,
} = salesforceDealApi;
